import { ErrorBoundary } from '@/components/ErrorBoundary/ErrorBoundaryReactRouter';
import { convertRouteModule } from '@/utils/convert-route-module';
import { GlobalWrapper } from '@/views/GlobalWrapper';
import { glCodesModalRoutes } from '@/views/payments/glCodesModalRoutes';
import { paymentAccountModalRoutes } from '@/views/payments/routes';
import { stripeAccountsModalRoutes } from '@/views/payments/stripeAccountsModalRoutes';
import { stickerRoutes } from '@/views/stickers/routes';
import React from 'react';
import { createBrowserRouter, redirect, type LoaderFunctionArgs } from 'react-router';

const routes = [
  {
    path: '/schools/:instituteId/attendance_plus/overview',
    lazy: () =>
      import('@/views/attendance_plus/components/Overview/Overview').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/attendance_plus/students/:studentId',
    lazy: () =>
      import('@/views/attendance_plus/components/StudentProfile/StudentProfile').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/districts/:instituteId/attendance_plus/school_year_setup',
    lazy: () =>
      import('@/views/attendance_plus/components/SchoolYearSetup/SchoolYearSetup').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/districts/:instituteId/attendance_plus/dashboard',
    lazy: () =>
      import('@/views/attendance_plus/components/Dashboard/Dashboard').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/districts/:instituteId/attendance_plus/settings',
    lazy: () =>
      import('@/views/attendance_plus/components/Settings/Settings.tsx').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/attendance_plus/task_list',
    lazy: () =>
      import('@/views/attendance_plus/components/TaskList/TaskList').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/parent_absence_reporting',
    lazy: () =>
      import('@/views/parent_absence_reporting/components/ParentAbsenceReporting').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/parent_absence_reporting/requests',
    lazy: () =>
      import('@/views/parent_absence_reporting/components/admin/AdminAbsenceReports').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/attendance_plus/reported_absences',
    lazy: () =>
      import('@/views/attendance_plus/components/ParentReportedAbsences/ParentReportedAbsences').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/attendance_plus/task_list_dashboard',
    lazy: () =>
      import('@/views/attendance_plus/components/TaskListDashboard/TaskListDashboard').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/:instituteType/:instituteId/attendance_plus/workflows',
    lazy: () =>
      import('@/views/workflows/components/WorkflowList/WorkflowList').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/:instituteType/:instituteId/attendance_plus/workflows/dashboard',
    lazy: () =>
      import('@/views/workflows/components/WorkflowDashboard/WorkflowDashboardLanding').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/:instituteType/:instituteId/attendance_plus/workflows/new',
    lazy: () =>
      import('@/views/workflows/components/WorkflowBuilder/WorkflowBuilder').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/:instituteType/:instituteId/attendance_plus/workflows/:workflowId/edit',
    lazy: () =>
      import('@/views/workflows/components/WorkflowBuilder/WorkflowBuilder').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/:instituteType/:instituteId/attendance_plus/workflows/:workflowId',
    lazy: () =>
      import('@/views/workflows/components/WorkflowDetails/WorkflowDetails').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/:instituteType/:instituteId/attendance_plus/workflows/:workflowId/dashboard',
    lazy: () =>
      import('@/views/workflows/components/WorkflowDashboard/WorkflowDashboard').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/:instituteType/:instituteId/attendance_plus/workflows/:workflowId/configure',
    lazy: () =>
      import('@/views/workflows/components/WorkflowConfiguration/WorkflowConfiguration').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/:instituteType/:instituteId/attendance_plus/workflows/:workflowId/task_definitions/:taskDefinitionId/tasks',
    lazy: () =>
      import('@/views/workflows/components/WorkflowTasks/WorkflowTasks').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/direct_message/templates',
    lazy: () =>
      import('@/views/direct_message/templates/components/Templates').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/districts/:instituteId/direct_message/templates',
    lazy: () =>
      import('@/views/direct_message/templates/components/Templates').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/districts/:instituteId/users/:userId/chats',
    lazy: () => import('@/views/direct_message/ChatPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/users/:userId/chats',
    lazy: () => import('@/views/direct_message/ChatPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/users/:userId/chats/:userChatThreadId',
    lazy: () => import('@/views/direct_message/ChatPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/districts/:instituteId/users/:userId/chats/:userChatThreadId',
    lazy: () => import('@/views/direct_message/ChatPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/users/:userId/chats/:userChatThreadId',
    lazy: () => import('@/views/direct_message/ChatPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/fee_kinds',
    lazy: () =>
      import('@/views/payments/components/InvoicesOverview/Overview').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/districts/:instituteId/fee_kinds',
    lazy: () =>
      import('@/views/payments/components/InvoicesOverview/Overview').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/fee_kinds/new',
    lazy: () =>
      import('@/views/payments/components/CreateEditInvoice/CreateInvoiceTemplate').then(
        convertRouteModule
      ),
    ErrorBoundary,
  },
  {
    path: '/districts/:instituteId/fee_kinds/new',
    lazy: () =>
      import('@/views/payments/components/CreateEditInvoice/CreateInvoiceTemplate').then(
        convertRouteModule
      ),
    ErrorBoundary,
  },
  {
    path: '/fee_kinds/:feeKindId/edit',
    lazy: () =>
      import('@/views/payments/components/CreateEditInvoice/EditInvoiceTemplate').then(
        convertRouteModule
      ),
    ErrorBoundary,
  },
  {
    path: '/fee_kinds/:feeKindId/fees',
    lazy: () =>
      import('@/views/payments/components/SendInvoice/SendInvoice').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/accounting_codes',
    lazy: () =>
      import('@/views/payments/components/AccountingCodes/AccountingCodes').then(
        convertRouteModule
      ),
    ErrorBoundary,
  },
  {
    path: '/districts/:instituteId/accounting_codes',
    lazy: () =>
      import('@/views/payments/components/AccountingCodes/AccountingCodes').then(
        convertRouteModule
      ),
    ErrorBoundary,
  },
  {
    path: '/districts/:instituteId/payment_accounts',
    lazy: () => import('@/views/payments/PaymentManagementPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
    children: paymentAccountModalRoutes,
  },
  {
    path: '/schools/:instituteId/payment_accounts',
    lazy: () => import('@/views/payments/PaymentManagementPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
    children: paymentAccountModalRoutes,
  },
  {
    path: '/districts/:instituteId/stripe_accounts',
    lazy: () =>
      import('@/views/payments/components/stripeAccounts/StripeAccountsPage').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
    children: stripeAccountsModalRoutes,
  },
  {
    path: '/schools/:instituteId/stripe_accounts',
    lazy: () =>
      import('@/views/payments/components/stripeAccounts/StripeAccountsPage').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
    children: stripeAccountsModalRoutes,
  },
  {
    path: '/districts/:instituteId/payment_transactions',
    lazy: () =>
      import('@/views/payments/components/PaymentsPage/PaymentsPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/payment_transactions',
    lazy: () =>
      import('@/views/payments/components/PaymentsPage/PaymentsPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/districts/:instituteId/payouts',
    lazy: () => import('@/views/payments/components/Payouts/PayoutsPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/payouts',
    lazy: () => import('@/views/payments/components/Payouts/PayoutsPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    // ReportsPage (PAY-891) hosts the Payments/Payouts tab bar and renders
    // each tab through <Outlet />. Children inherit `embeddedInHub` so they
    // hide their own page title (PAY-893 will lift the date filter too).
    path: '/districts/:instituteId/reports',
    lazy: () =>
      import('@/views/payments/components/Reporting/ReportsPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
    children: [
      {
        index: true,
        loader: ({ params }: LoaderFunctionArgs) => {
          const { instituteId } = params;
          if (!instituteId) throw new Response('Missing instituteId', { status: 404 });
          return redirect(`/districts/${instituteId}/reports/payment_transactions`);
        },
      },
      {
        path: 'payment_transactions',
        lazy: () =>
          import('@/views/payments/components/PaymentsPage/PaymentsPage').then(convertRouteModule),
        HydrateFallback: () => null,
      },
      {
        path: 'payouts',
        lazy: () =>
          import('@/views/payments/components/Payouts/PayoutsPage').then(convertRouteModule),
        HydrateFallback: () => null,
      },
      {
        path: 'payouts_by_gl_codes',
        lazy: () =>
          import('@/views/payments/components/GlCodePayouts/GlCodePayoutsPage').then(
            convertRouteModule
          ),
        HydrateFallback: () => null,
      },
    ],
  },
  {
    path: '/schools/:instituteId/reports',
    lazy: () =>
      import('@/views/payments/components/Reporting/ReportsPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
    children: [
      {
        index: true,
        loader: ({ params }: LoaderFunctionArgs) => {
          const { instituteId } = params;
          if (!instituteId) throw new Response('Missing instituteId', { status: 404 });
          return redirect(`/schools/${instituteId}/reports/payment_transactions`);
        },
      },
      {
        path: 'payment_transactions',
        lazy: () =>
          import('@/views/payments/components/PaymentsPage/PaymentsPage').then(convertRouteModule),
        HydrateFallback: () => null,
      },
      {
        path: 'payouts',
        lazy: () =>
          import('@/views/payments/components/Payouts/PayoutsPage').then(convertRouteModule),
        HydrateFallback: () => null,
      },
      {
        path: 'payouts_by_gl_codes',
        lazy: () =>
          import('@/views/payments/components/GlCodePayouts/GlCodePayoutsPage').then(
            convertRouteModule
          ),
        HydrateFallback: () => null,
      },
    ],
  },
  {
    // PaySetupShell (PAY-890) hosts the Setup tab bar and renders each tab
    // through <Outlet />. Children inherit `embeddedInHub` so they hide their
    // own page title. The index loader always redirects to `stripe_accounts`;
    // if that tab is hidden by feature flags, the shell itself redirects to
    // the first visible tab. The router can't read flags, so the second hop
    // lives inside <PaySetupShell />.
    path: '/districts/:instituteId/setup',
    lazy: () => import('@/views/payments/hubs/PaySetupShell').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
    children: [
      {
        index: true,
        loader: ({ params }: LoaderFunctionArgs) => {
          const { instituteId } = params;
          if (!instituteId) throw new Response('Missing instituteId', { status: 404 });
          return redirect(`/districts/${instituteId}/setup/stripe_accounts`);
        },
      },
      {
        path: 'stripe_accounts',
        lazy: () =>
          import('@/views/payments/components/stripeAccounts/StripeAccountsPage').then(
            convertRouteModule
          ),
        children: stripeAccountsModalRoutes,
        HydrateFallback: () => null,
      },
      {
        path: 'payment_accounts',
        lazy: () => import('@/views/payments/PaymentManagementPage').then(convertRouteModule),
        children: paymentAccountModalRoutes,
        HydrateFallback: () => null,
      },
      {
        path: 'gl_codes',
        lazy: () => import('@/views/payments/components/GlCodes/GlCodes').then(convertRouteModule),
        children: glCodesModalRoutes,
        HydrateFallback: () => null,
      },
    ],
  },
  {
    path: '/schools/:instituteId/setup',
    lazy: () => import('@/views/payments/hubs/PaySetupShell').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
    children: [
      {
        index: true,
        loader: ({ params }: LoaderFunctionArgs) => {
          const { instituteId } = params;
          if (!instituteId) throw new Response('Missing instituteId', { status: 404 });
          return redirect(`/schools/${instituteId}/setup/stripe_accounts`);
        },
      },
      {
        path: 'stripe_accounts',
        lazy: () =>
          import('@/views/payments/components/stripeAccounts/StripeAccountsPage').then(
            convertRouteModule
          ),
        children: stripeAccountsModalRoutes,
        HydrateFallback: () => null,
      },
      {
        path: 'payment_accounts',
        lazy: () => import('@/views/payments/PaymentManagementPage').then(convertRouteModule),
        children: paymentAccountModalRoutes,
        HydrateFallback: () => null,
      },
      {
        path: 'gl_codes',
        lazy: () => import('@/views/payments/components/GlCodes/GlCodes').then(convertRouteModule),
        children: glCodesModalRoutes,
        HydrateFallback: () => null,
      },
    ],
  },
  {
    path: '/accounting_codes/:accounting_code_id',
    lazy: () =>
      import('@/views/payments/components/AccountingCodes/AccountingCodeReport').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/cannot_combine_account',
    lazy: () => import('@/views/users/components/CannotCombineAccount').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/join_school/new',
    lazy: () => import('@/views/join_school/components/JoinSchoolPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/confirm_combine_account',
    lazy: () => import('@/views/users/components/ConfirmCombineAccount').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:schoolId/students/:studentId',
    lazy: () => import('@/views/student_profile/StudentProfileView').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/students/:studentId',
    lazy: () => import('@/views/student_profile/StudentProfileView').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/students/:student_id/communications_dashboard',
    lazy: () =>
      import('@/views/student_communications/components/StudentCommunicationsDashboard').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/:instituteType/:instituteId/virtual_phone/*',
    lazy: () => import('@/views/virtual_phone/VirtualPhoneApp').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  ...stickerRoutes,
  {
    path: '/:instituteType/:instituteId/smart_alerts/templates',
    lazy: () =>
      import('@/views/smart_alerts/components/MessageLibrary/MessageLibrary').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/:instituteType/:instituteId/smart_alerts/notifications/:notificationId',
    lazy: () =>
      import('@/views/smart_alerts/components/DeliveryCsvExportPortals').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/users/:chatId/chats',
    lazy: () => import('@/views/direct_message/ChatPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/users/:chatId/chats/:userChatThreadId',
    lazy: () => import('@/views/direct_message/ChatPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/districts/:instituteId/broadcasts/templates/new',
    lazy: () =>
      import('@/views/auto_notices/components/Template/SetupPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/broadcasts/templates/new',
    lazy: () =>
      import('@/views/auto_notices/components/Template/SetupPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/districts/:instituteId/broadcasts/templates/:templateId/edit',
    lazy: () =>
      import('@/views/auto_notices/components/Template/SetupPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/broadcasts/templates/:templateId/edit',
    lazy: () =>
      import('@/views/auto_notices/components/Template/SetupPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/broadcasts/templates/:templateId/edit',
    lazy: () =>
      import('@/views/auto_notices/components/Template/SetupPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/:instituteType/:instituteId/groups',
    lazy: () => import('@/views/groups/components/GroupsIndex').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/groups/:groupId/edit',
    lazy: () => import('@/views/groups/components/ManageGroup').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/:instituteType/:instituteId/dashboard/browse',
    lazy: () =>
      import('@/views/reachability/components/ReachabilityDashboard').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/districts/:instituteId/roster/prepare_classes',
    lazy: () =>
      import('@/views/data_assistant/class_management/components/ClassManagementPage').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/roster/prepare_classes',
    lazy: () =>
      import('@/views/data_assistant/class_management/components/ClassManagementPage').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/internal/cms_chatbot',
    lazy: () => import('@/views/chatbot/components/Chatbot').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/internal/sync_error_definitions',
    lazy: () =>
      import('@/views/sync_error_intelligence/components/SyncErrorDefinitionsPage').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/internal/sync_error_definitions/:uuid',
    lazy: () =>
      import('@/views/sync_error_intelligence/components/DefinitionDetailPage').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/moderation_dashboard',
    lazy: () =>
      import('@/views/moderation/components/ModerationDashboard').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/districts/:instituteId/moderation_dashboard',
    lazy: () =>
      import('@/views/moderation/components/ModerationDashboard').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/join_school_requests',
    lazy: () =>
      import('@/views/join_school_requests/components/JoinSchoolRequestsPage').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/districts/:instituteId/join_school_requests',
    lazy: () =>
      import('@/views/join_school_requests/components/JoinSchoolRequestsPage').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  // Secure Documents feature
  {
    path: '/schools/:instituteId/secure_documents',
    lazy: () =>
      import('@/views/secure_documents/components/ParentSecureDocumentsPage').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/districts/:instituteId/secure_documents',
    lazy: () =>
      import('@/views/secure_documents/components/ParentSecureDocumentsPage').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/student_secure_documents/:studentSecureDocumentId/secure_document_signature/new',
    lazy: () =>
      import('@/views/secure_documents/components/SecureDocumentSignaturePage').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  // Self-service Upgrades feature
  {
    path: '/schools/:instituteId/upgrades',
    lazy: () => import('@/views/upgrades/components/UpgradesPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/districts/:instituteId/upgrades',
    lazy: () => import('@/views/upgrades/components/UpgradesPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  // Apple messages report (district + single-site school). Both mount the shared
  // sqkt_app entrypoint and read their institute context from the bootstrapped app context.
  {
    path: '/districts/:instituteId/apple_messages',
    lazy: () =>
      import('@/views/apple_messages/components/invitations/AppleMessagesInvitations').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/apple_messages',
    lazy: () =>
      import('@/views/apple_messages/components/invitations/AppleMessagesInvitations').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/main_secure_documents/:mainSecureDocumentId',
    lazy: () =>
      import('@/views/secure_documents/components/admin/MainSecureDocumentSentReportPage').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/users/:userId/user_notification_preferences',
    lazy: () =>
      import('@/views/notification_settings/components/screen/NotificationSettingsRoute').then(
        convertRouteModule
      ),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  // Records Request (tool + history). PS-admin cross-institute routes live under /internal; the
  // institute-scoped /districts|schools/:instituteId variants are served by the customer-facing
  // controller (with a PS-admin bypass on its gate). All six mount the shared sqkt_app entrypoint
  // and read their preselected institute / date range from the bootstrapped app context.
  {
    path: '/internal/records_request',
    lazy: () => import('@/views/records_request/RecordsRequestApp').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/districts/:instituteId/records_request',
    lazy: () => import('@/views/records_request/RecordsRequestApp').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/records_request',
    lazy: () => import('@/views/records_request/RecordsRequestApp').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/internal/records_request/history',
    lazy: () => import('@/views/records_request/RequestHistoryPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/districts/:instituteId/records_request/history',
    lazy: () => import('@/views/records_request/RequestHistoryPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/records_request/history',
    lazy: () => import('@/views/records_request/RequestHistoryPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/schools/:instituteId/users/:userId/contact_log',
    lazy: () => import('@/views/contact_log/components/ContactLogPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/districts/:instituteId/users/:userId/contact_log',
    lazy: () => import('@/views/contact_log/components/ContactLogPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/counties/:instituteId/users/:userId/contact_log',
    lazy: () => import('@/views/contact_log/components/ContactLogPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '/users/:userId/contact_log',
    lazy: () => import('@/views/contact_log/components/ContactLogPage').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
  {
    path: '*',
    lazy: () => import('@/routes/NotFound').then(convertRouteModule),
    ErrorBoundary,
    HydrateFallback: () => null,
  },
];

export const router = createBrowserRouter([
  {
    element: <GlobalWrapper />,
    children: routes,
  },
]);
